import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import dayjs from "dayjs";
import { MigrateFile } from "../utils/helpers/types";
import Table, { Column } from "../components/table/Table";
import { initialPaginationData } from "../utils/constants";
import LayoutContext from "../context/layout-context";

import Button from "../components/button/Button";
import Modal from "../components/modal/Modal";
import FileUploader from "../components/file-uploader/FileUploader";
import { getHistory, uploadFile } from "../utils/helpers/data/key-migration";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import styles from "./migrate-key.module.scss";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";

const KeyMigration: FunctionComponent = () => {
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [reset, setReset] = useState(false);
  const [dataSource, setDataSource] = useState<MigrateFile[]>([]);
  const [startDate, setStartDate] = useState<string>("");

  const [endDate, setEndDate] = useState<string>("");

  const [actor, setActor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [migrateKeysOnView, setMigrateKeysOnView] =
    useState<MigrateFile | null>(null);

  const displayAddEditModal = () => {
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setShowAddEditModal(false);
  };

  const { notify } = useContext(LayoutContext);

  const fetchHistory = async (pageNumber = pagination.pageNumber) => {
    setLoading(true);

    const { error, message, data } = await getHistory(
      pageNumber,
      pagination.pageSize,
      startDate || "2023-01-01",
      endDate || dayjs().add(1, "day").format("YYYY-MM-DD"),
      actor || undefined
    );

    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch history: ${message}`);
      return;
    }

    setDataSource(data.records);
    setPagination({ ...pagination, total: data.total, pageNumber });
  };

  const handleViewReport = async () => {
    setLoading(true);

    try {
      setPagination({ ...pagination, pageNumber: 1 });

      await fetchHistory(1);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify(
        "error",
        "An error occurred while fetching migration key history.",
        error
      );
    }
  };

  const resetFilters = () => {
    setReset(true);
    setStartDate("");
    setEndDate("");
    setActor("");
    setPagination({ ...pagination, pageNumber: 1 });
  };

  useEffect(() => {
    if (reset) {
      fetchHistory();
      setReset(false);
    }
  }, [reset, startDate, endDate, actor]);

  const showResetFilter = startDate || endDate || actor;

  const columns: Column<MigrateFile>[] = [
    {
      title: "Actor",
      dataIndex: "actor",
      key: "actor"
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName"
    },
    {
      title: "Start Time",
      dataIndex: "startTime",
      key: "startTime"
    },
    {
      title: "End Time",
      dataIndex: "endTime",
      key: "endTime"
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span
          className={`tag smaller ${
            status === "SUCCESSFUL" ? "success" : "error"
          }`}
        >
          {status === "SUCCESSFUL" ? "SUCCESSFUL" : "FAILED"}
        </span>
      )
    },
    {
      title: "",
      render: (_: unknown, migrate: MigrateFile) => {
        const options: ContextOption[] = [
          {
            label: "View details",
            onClick: () => setMigrateKeysOnView(migrate)
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];

  useEffect(() => {
    fetchHistory();
  }, [pagination.pageNumber, pagination.pageSize]);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Key Migration</h1>

      <div className={styles.cont}>
        <div className={styles.containerFlex}>
          <div className={styles.container}>
            <div className="input-group">
              <Input
                type="date"
                value={startDate || ""}
                onChange={value => setStartDate(value as string)}
                placeholder="Enter start date"
                responsive
              />
            </div>

            <div className="input-group ">
              <Input
                type="date"
                value={endDate || ""}
                onChange={value => setEndDate(value as string)}
                placeholder="Enter end date"
                responsive
              />
            </div>

            <div className="input-group ">
              <Input
                value={actor || ""}
                onChange={value => setActor(value as string)}
                placeholder="Enter actor"
                responsive
              />
            </div>
          </div>
        </div>

        <div className={styles.inputView}>
          <Button
            type="accent"
            size="large"
            className="strong no-pdf-print"
            onClick={handleViewReport}
          >
            Filter
          </Button>
          <span className={styles.button} />
        </div>
      </div>

      {showResetFilter && (
        <Button type="accent" size="small" onClick={resetFilters}>
          RESET
        </Button>
      )}
      <br />

      <div className="flex end vertical-margin spaced">
        <Button onClick={() => displayAddEditModal()}>Migrate Key</Button>
      </div>

      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onPageChange={pageNumber =>
          setPagination({ ...pagination, pageNumber })
        }
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
      />

      <AddEditModal
        visible={showAddEditModal}
        cancel={() => dismissAddEditModal()}
        onComplete={stayOnPage =>
          fetchHistory(stayOnPage ? pagination.pageNumber : 1)
        }
      />

      <MigrateKeyViewModal
        migrateKey={migrateKeysOnView}
        cancel={() => setMigrateKeysOnView(null)}
        visible={Boolean(migrateKeysOnView)}
      />
    </main>
  );
};

interface AddEditProps {
  visible: boolean;
  cancel: () => void;
  onComplete: (stayOnPage: boolean) => void;
}

const AddEditModal: FunctionComponent<AddEditProps> = ({
  visible,
  cancel,
  onComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const { notify } = useContext(LayoutContext);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!file) {
      notify("error", "Please select a file to upload.");
      setLoading(false);
      return;
    }

    const { error, message } = await uploadFile(file);
    setLoading(false);

    if (error) {
      notify("error", `Unable to upload key file: ${message}`);
    } else {
      notify("success", message, 20000);
      setFile(null);
      onComplete(false);
      cancel();
    }
  };

  return (
    <Modal visible={visible} cancel={cancel} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Upload key file</span>
          <FileUploader
            acceptedTypes={[]}
            titleText="Upload key file"
            subtitle="Or drag key file to upload"
            file={file}
            onChange={setFile}
          />
        </div>

        <div className="vertical-margin flex end spaced">
          <Button type="accent" onClick={cancel} size="small">
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            Upload key file
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface migrateKeyModalProps {
  migrateKey: MigrateFile | null;
  cancel: () => void;
  visible: boolean;
}

const MigrateKeyViewModal: FunctionComponent<migrateKeyModalProps> = ({
  migrateKey: _migrateKey,
  cancel,
  visible
}) => {
  const [loading] = useState(false);
  const [migrateKey, setMigrateKey] = useState<MigrateFile | null>(_migrateKey);

  useEffect(() => {
    setMigrateKey(_migrateKey);
  }, [_migrateKey]);

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{migrateKey?.id || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Actor:</span>
            <span className={styles.value}>{migrateKey?.actor || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>File Name:</span>
            <span className={styles.value}>
              {migrateKey?.fileName || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Status:</span>
            <span className={styles.value}>{migrateKey?.status || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created Date:</span>
            <span className={styles.value}>
              {migrateKey?.createdOn ? formatDate(migrateKey.createdOn) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Start Time:</span>
            <span className={styles.value}>
              {migrateKey?.startTime || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>End Time:</span>
            <span className={styles.value}>{migrateKey?.endTime || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Updated Date:</span>
            <span className={styles.value}>
              {migrateKey?.lastUpdatedOn
                ? formatDate(migrateKey.lastUpdatedOn)
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Summary:</span>
            <span className={styles.value} style={{ whiteSpace: "pre-wrap" }}>
              {migrateKey?.summary?.replace(/Total/g, "\nTotal") || "N/A"}
            </span>
          </div>
        </section>
      )}
    </Modal>
  );
};
export default KeyMigration;
